import React, { useEffect, useRef } from 'react';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { getMoodColor, toRGBA } from './utils';

// Register Chart.js components
Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend
);

export interface LineChartDataPoint {
  label: string;
  value: number;
}

export interface LineChartProps {
  /** Data points with labels and values */
  data: LineChartDataPoint[];
  /** Container height in pixels */
  height?: number;
  /** Whether to show legend */
  showLegend?: boolean;
  /** Chart title */
  title?: string;
  /** Y-axis minimum value */
  minY?: number;
  /** Y-axis maximum value */
  maxY?: number;
  /** Whether to use mood colors for points */
  useMoodColors?: boolean;
  /** Y-axis label */
  yAxisLabel?: string;
  /** X-axis label */
  xAxisLabel?: string;
}

export function LineChart({
  data,
  height = 300,
  showLegend = false,
  title,
  minY,
  maxY,
  useMoodColors = true,
  yAxisLabel,
  xAxisLabel,
}: LineChartProps) {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    if (!chartRef.current || data.length === 0) return;

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    const labels = data.map(d => d.label);
    const values = data.map(d => d.value);

    // Calculate colors
    let borderColors: string[];
    let backgroundColors: string[];

    if (useMoodColors) {
      borderColors = values.map(value => getMoodColor(value));
      backgroundColors = borderColors.map(c => toRGBA(c, 0.9));
    } else {
      borderColors = values.map(() => 'rgb(102, 126, 234)');
      backgroundColors = borderColors.map(c => toRGBA(c, 0.9));
    }

    const scales: any = {};

    // Y axis config
    scales.y = {};
    if (minY !== undefined) scales.y.min = minY;
    if (maxY !== undefined) scales.y.max = maxY;
    if (yAxisLabel) {
      scales.y.title = { display: true, text: yAxisLabel };
    }

    // X axis config
    if (xAxisLabel) {
      scales.x = {
        title: { display: true, text: xAxisLabel },
      };
    }

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            data: values,
            borderColor: borderColors,
            backgroundColor: backgroundColors,
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            fill: false,
            tension: 0.1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: showLegend },
          title: title ? { display: true, text: title } : undefined,
        },
        scales,
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data, showLegend, title, minY, maxY, useMoodColors, yAxisLabel, xAxisLabel]);

  if (data.length === 0) {
    return <div style={{ height }} className="d-flex align-items-center justify-content-center text-muted">No data</div>;
  }

  return (
    <div style={{ height }}>
      <canvas ref={chartRef} />
    </div>
  );
}

export default LineChart;
